import type { TailorPreview, TailorSkillLine } from '~/utils/cvTailor'
import type { MaybeRefOrGetter } from 'vue'
import type { CVData, CVTemplate } from '~/types'
import type { PremiumPlanId } from '~/types/premium'
import { isCvLimitFetchError } from '~/composables/useCvLimit'
import { normalizeWelcomeCode, WELCOME_CODE } from '~/constants/pricing'

/**
 * Everything a finished CV can be turned into, in one place.
 *
 * The ad landing page and the homepage generator both end on the same decision —
 * free watermarked PDF, one clean PDF, the Job Search Pack, or keep the CV in an
 * account — so the checkout plumbing lives here instead of in either page:
 * parking the CV for a guest purchase, watching for the webhook while the
 * checkout iframe is open, and handing the file over the moment it lands.
 *
 * Nothing here needs an account except the pack, which has to put the web CV and
 * the 30 days of Premium somewhere.
 */
export interface CvOffersOptions {
  /** The finished CV, or null before there is one. */
  cv: MaybeRefOrGetter<CVData | null>
  /** PDF layout the buyer is looking at; it is what gets parked and saved. */
  template: MaybeRefOrGetter<CVTemplate>
  /** Where a sign-up came from, e.g. `ai-generator`; shows up in /registration. */
  signupSource?: string
  /** `guest_cv_generations` id of this CV, so its downloads show up in admin. */
  generationId?: MaybeRefOrGetter<string | null | undefined>
  /** The account CV this one already is (the app's chat builder): saving updates it instead of adding another. */
  cvId?: MaybeRefOrGetter<string | null | undefined>
  /** Added to every journey event from here, e.g. the result-template experiment and its variant. */
  trackingData?: MaybeRefOrGetter<Record<string, unknown> | undefined>
  /**
   * The tailored CV /api/ai/tailor-preview parked for sale (`tailored-pdf`);
   * buying that plan pays for this token instead of parking the CV on screen.
   */
  tailorToken?: MaybeRefOrGetter<string | null | undefined>
  /** Skills from the posting the visitor ticked as theirs: the tailored PDF lists them. */
  confirmedSkills?: MaybeRefOrGetter<string[] | undefined>
  /** For ticked skills, the lines on where the visitor used them: the tailored PDF adds them under those roles. */
  skillLines?: MaybeRefOrGetter<TailorSkillLine[] | undefined>
}

/** What LandingResultOffers exposes for the download button in the preview's corner (LandingPreviewDownload) and for its tailoring card. */
export interface CornerDownload {
  cornerLabel: string
  cornerBusy: boolean
  /** The click asks for money (green); otherwise the file just goes down. */
  cornerPaid: boolean
  cornerDownload: () => void
  /** "Make it online" on the web CV preview. */
  websiteLabel: string
  cornerWebsite: () => void
  /** Opens the tailoring card's posting field (`#cv-tailor`), ready for a posting. */
  openTailor: () => void
  /** Shows a tailoring run earlier again, as it was. */
  restoreTailor: (preview: TailorPreview, posting: string | null) => void
  /** The pack is on sale to this visitor. */
  canBuyPack: boolean
  /** Its checkout is opening. */
  buyingPack: boolean
  buyPack: () => void
  /** Save the CV and open the editor; signed out, an account first. */
  editCv: () => void
}

/**
 * A purchase from the offers; `tailored-pdf` and `application-kit` are only on
 * sale once a tailoring preview parked one: both pay for that parked tailoring.
 */
export type OfferPlanId = 'one-time' | 'tailored-pdf' | 'application-kit' | 'job-pack'

/** Plans bought on the tailoring a preview parked, not on the CV on screen. */
const onTailoring = (planId: OfferPlanId) => planId === 'tailored-pdf' || planId === 'application-kit'

/** How often the account or the parked CV is asked whether the payment landed. */
const POLL_INTERVAL_MS = 3000
/** ~5 minutes: long enough for a slow card, short enough to stop by itself. */
const MAX_POLLS = 100

export function useCvOffers(options: CvOffersOptions) {
  const { t, locale } = useI18n()
  const { isAuthenticated } = useAuthUser()
  const { exportToPDF, isExporting, lastErrorStatus } = usePuppeteerExport()
  const journey = useJourney()
  const { createCheckoutSession, isScriptLoading } = useLemonSqueezy()
  const { getPlan, jobPackPrice, exportCreditPrice } = usePremiumPlans()
  const { setPendingCV, setPendingTemplate, setPendingGeneration, setPendingClaimToken, hasPendingPosting, hasPendingInterview } = usePendingPurchase()
  const { promptNewsletter } = useNewsletterPrompt()
  const route = useRoute()
  /** The welcome e-mail's code (?promo=HELLO-…), filled in at the pack's checkout only. */
  const welcomeCode = (planId: string) => (planId === 'job-pack' ? normalizeWelcomeCode(route.query[WELCOME_CODE.param]) ?? undefined : undefined)
  const { isPremium, exportCredits, isChecked: isPlanKnown, checkPremiumStatus } = usePremiumStatus()
  const { claimAndOpen } = useGuestClaim()
  const { trackPurchase } = useAdsConversion()
  const user = useSupabaseUser()

  const cv = () => toValue(options.cv)
  const template = () => toValue(options.template)
  /** `Jan_Novak-inmyresume.pdf`, or just `cv-…` when the AI found no name. */
  const fileBase = () => cv()?.name?.trim().replace(/\s+/g, '_') || 'cv'

  const error = ref<string | null>(null)
  const isStartingCheckout = ref(false)
  /** A paid pack is being handed to the new account. */
  const isClaiming = ref(false)
  const checkoutUrl = ref<string | null>(null)
  const showSignup = ref(false)
  const confirmEmailSentTo = ref<string | null>(null)
  /** Which paid route was picked; drives the sign-up label and the spinner. */
  const checkoutPlanId = ref<OfferPlanId>('job-pack')

  /**
   * Nothing is for sale to somebody who already owns it: a visitor with an
   * active plan or pack gets the clean download and the editor, not a price
   * list. The plan has to be *known* to be ruled out, so the cards wait for the
   * check rather than assuming "free" and correcting themselves a moment later.
   */
  const showPaidOffers = computed(() => isPlanKnown.value && !isPremium.value)
  const canBuyPack = computed(() => showPaidOffers.value && !!getPlan('job-pack'))
  const canBuySingle = computed(() => showPaidOffers.value && !!getPlan('one-time'))
  /** Only with a tailoring parked for sale: the preview decides, by sending its token. */
  const canBuyTailored = computed(() => showPaidOffers.value && !!getPlan('tailored-pdf') && !!toValue(options.tailorToken))
  /** The same tailoring with its cover letter and interview prep (PRICES.applicationKit). */
  const canBuyKit = computed(() => showPaidOffers.value && !!getPlan('application-kit') && !!toValue(options.tailorToken))
  const signupOfferLabel = computed(() => checkoutPlanId.value === 'one-time'
    ? `${t('landing.builder.single.title')} – ${exportCreditPrice}`
    : `${t('premium.jobPack.title')} – ${jobPackPrice}`)

  const extra = () => toValue(options.trackingData) ?? {}

  /** A download attempt for /admin/journeys; a failed one says why (429 = daily limit). */
  function trackDownload(kind: 'free' | 'paid', ok: boolean) {
    journey.track('cv_download', {
      generation_id: toValue(options.generationId) ?? null,
      source: options.signupSource ?? null,
      kind,
      template: template(),
      ok,
      ...(!ok && { status: lastErrorStatus.value ?? 0 }),
      ...extra(),
    })
  }

  /** The newsletter prompt that follows a free download, a few seconds later. */
  function followUpDownload() {
    const cvData = cv()
    promptNewsletter(4000, { email: cvData?.contact?.email, generationId: toValue(options.generationId) })
  }

  /**
   * Free, watermarked PDF. Works signed out; the server meters it per day.
   * `followUp: false` leaves out the newsletter prompt, for a page that offers
   * something else right after the download (it can call followUpDownload).
   */
  async function downloadFree({ followUp = true }: { followUp?: boolean } = {}): Promise<boolean> {
    const cvData = cv()
    if (!cvData || isExporting.value) return false

    const ok = await exportToPDF({
      cvData: { ...cvData, showQRCode: false },
      template: template(),
      filename: `${fileBase()}-inmyresume.pdf`,
      generationId: toValue(options.generationId),
    })
    trackDownload('free', ok)
    if (ok && followUp) followUpDownload()
    return ok
  }

  /*
   * Guest purchase of the single clean PDF.
   *
   * The token is the whole receipt: it unlocks one snapshot of this CV, works on
   * any device for a month, and is e-mailed to the buyer by the webhook. Nothing
   * here asks for an account, a password or an e-mail address — LemonSqueezy
   * collects the address it needs during checkout.
   */
  const guestToken = ref<string | null>(null)
  const guestPlan = ref<OfferPlanId>('one-time')
  const guestPaid = ref(false)
  let guestPoll: ReturnType<typeof setInterval> | null = null

  /** Permanent home of the purchase, for the buyer who closes this tab. */
  const guestDownloadUrl = computed(() => guestToken.value ? `/download/${guestToken.value}` : null)
  /** Where an unfinished pack can be picked up again; also sent by e-mail. */
  const guestClaimUrl = computed(() => guestToken.value ? `/claim/${guestToken.value}` : null)
  /** The pack is paid for and only needs an account now. */
  const packAwaitingAccount = computed(() => guestPaid.value && guestPlan.value === 'job-pack')

  function stopGuestPoll() {
    if (guestPoll) {
      clearInterval(guestPoll)
      guestPoll = null
    }
  }

  /**
   * Parks the finished CV server-side so the payment has something to unlock.
   * The tailored PDF is already parked by its preview: this only brings that
   * row up to date with the layout and photo on screen.
   */
  async function parkCV(planId: OfferPlanId): Promise<string | null> {
    const cvData = cv()
    if (!cvData) return null
    const tailorToken = onTailoring(planId) ? toValue(options.tailorToken) : undefined
    if (onTailoring(planId) && !tailorToken) return null

    try {
      const response = await $fetch<{ success: boolean; token: string }>('/api/guest-export/start', {
        method: 'POST',
        body: {
          cvData,
          template: template(),
          locale: locale.value,
          // The kit pays for the tailoring parked as `tailored-pdf`: that row is brought up to date.
          planId: onTailoring(planId) ? 'tailored-pdf' : planId,
          tailorToken,
          confirmedSkills: tailorToken ? toValue(options.confirmedSkills) : undefined,
          skillLines: tailorToken ? toValue(options.skillLines) : undefined,
          generationId: toValue(options.generationId) ?? undefined,
          visitorId: journey.visitorId() ?? undefined,
        },
      })
      return response.success ? response.token : null
    } catch (err) {
      console.error('Could not park CV before guest checkout:', err)
      return null
    }
  }

  /** The clean PDF this payment bought; the server renders it from the parked snapshot. */
  async function downloadPaid() {
    if (!guestToken.value || isExporting.value) return
    const ok = await exportToPDF({
      template: template(),
      filename: `${fileBase()}.pdf`,
      guestToken: guestToken.value,
      generationId: toValue(options.generationId),
    })
    trackDownload('paid', ok)
  }

  /**
   * True once the webhook has landed. The single PDF is handed over on the spot;
   * the pack has nowhere to go until there is an account, so it opens the
   * sign-up instead — the money is already in, this is the last step.
   */
  async function checkGuestPayment(token: string): Promise<boolean> {
    try {
      const { status } = await $fetch<{ status: string }>(`/api/guest-export/${token}`)
      if (status !== 'paid') return false
    } catch {
      // Network hiccup: the next tick tries again, and the e-mail is the fallback.
      return false
    }

    stopGuestPoll()
    guestPaid.value = true
    checkoutUrl.value = null
    trackPurchase(guestPlan.value, token)
    trackPaid(guestPlan.value, true)

    if (guestPlan.value === 'job-pack') {
      // Parked first: signing up with Google leaves the page, and /auth/callback
      // finishes the claim from there.
      setPendingClaimToken(token)
      showSignup.value = true
      return true
    }

    await downloadPaid()
    // The kit's cover letter and interview prep are on its own page, written there.
    if (guestPlan.value === 'application-kit') await navigateTo(`/download/${token}`)
    return true
  }

  /**
   * Watches the parked CV while the checkout iframe is open. LemonSqueezy's own
   * redirect lands inside that iframe, so this is what lets the visitor stay on
   * the page and simply receive the file.
   */
  function watchGuestPayment(token: string) {
    stopGuestPoll()
    let attempts = 0
    guestPoll = setInterval(async () => {
      attempts++
      if (attempts > MAX_POLLS) {
        stopGuestPoll()
        return
      }
      await checkGuestPayment(token)
    }, POLL_INTERVAL_MS)
  }

  function trackCheckout(plan: string, guest: boolean) {
    journey.track('cv_checkout_open', { generation_id: toValue(options.generationId) ?? null, plan, guest, ...extra() })
  }

  /** The paid checkout, next to the opened one, so a variant can be judged by what it sold. */
  function trackPaid(plan: string, guest: boolean) {
    journey.track('cv_purchase', { generation_id: toValue(options.generationId) ?? null, plan, guest, ...extra() })
  }

  async function openGuestCheckout(planId: OfferPlanId) {
    const plan = getPlan(planId)
    if (!plan || !cv()) return

    isStartingCheckout.value = true
    error.value = null
    try {
      // Parked fresh every time, so a photo or template picked after the last
      // attempt is part of what the buyer actually pays for.
      const token = await parkCV(planId)
      if (!token) {
        error.value = t('premium.checkoutError')
        return
      }
      guestToken.value = token
      guestPlan.value = planId

      const url = await createCheckoutSession({
        variantId: plan.variantId,
        planId: plan.id,
        guestToken: token,
        discountCode: welcomeCode(plan.id),
        // Where LemonSqueezy's own redirect lands if the visitor follows it:
        // the file for a single PDF, the last step for a pack.
        redirectUrl: `${window.location.origin}/${planId === 'job-pack' ? 'claim' : 'download'}/${token}`,
      })

      if (!url) {
        error.value = t('premium.checkoutError')
        return
      }
      checkoutUrl.value = url
      trackCheckout(planId, true)
      watchGuestPayment(token)
    } catch (err) {
      console.error('Failed to open guest checkout:', err)
      error.value = t('premium.checkoutError')
    } finally {
      isStartingCheckout.value = false
    }
  }

  /** The last save was refused: a free account keeps one CV (server/utils/cvLimit.ts). */
  const cvLimitReached = ref(false)

  /** Saves the CV onto the current account so a purchase has something to unlock. */
  async function persistCV(): Promise<string | null> {
    const cvData = cv()
    if (!cvData) return null

    cvLimitReached.value = false
    try {
      const response = await $fetch<{ success: boolean; cvId: string }>('/api/cv/create-from-import', {
        method: 'POST',
        body: { cvData, template: template(), generationId: toValue(options.generationId), cvId: toValue(options.cvId) },
      })
      return response.success ? response.cvId : null
    } catch (err) {
      if (isCvLimitFetchError(err)) {
        cvLimitReached.value = true
        return null
      }
      console.error('Could not save CV before checkout:', err)
      return null
    }
  }

  /**
   * Where a paid checkout lands: `export_paid` offers the download, `pack_paid`
   * waits for Premium. A pack bought after comparing the CV with a posting
   * opens the editor on tailoring it to that posting.
   */
  function paidCvPath(planId: PremiumPlanId, cvId: string | null) {
    const flag = planId === 'one-time' ? 'export_paid=true' : 'pack_paid=true'
    if (!cvId) return `/cv?${flag}`
    const section = planId === 'job-pack' && (hasPendingPosting() || hasPendingInterview()) ? '&section=tailor' : ''
    return `/cv/${cvId}?${flag}${section}`
  }

  function returnUrl(planId: PremiumPlanId, cvId: string | null) {
    const base = typeof window !== 'undefined' ? window.location.origin : ''
    return `${base}${paidCvPath(planId, cvId)}`
  }

  /*
   * Account purchase (the pack, and any plan for someone already signed in).
   * The redirect lands inside the checkout iframe just the same, so this waits
   * for the webhook and then takes the buyer to the CV it unlocked, instead of
   * leaving them on a page that still offers to sell it to them.
   */
  const packCvId = ref<string | null>(null)
  let packPoll: ReturnType<typeof setInterval> | null = null
  /** The account as it was when the checkout opened; the single PDF only adds a credit. */
  let packBaseline = { isPremium: false, exportCredits: 0 }

  function stopPackPoll() {
    if (packPoll) {
      clearInterval(packPoll)
      packPoll = null
    }
  }

  async function checkPackPayment(): Promise<boolean> {
    await checkPremiumStatus()
    const paid = checkoutPlanId.value === 'one-time'
      ? exportCredits.value > packBaseline.exportCredits
      : isPremium.value && !packBaseline.isPremium
    if (!paid) return false

    stopPackPoll()
    checkoutUrl.value = null
    trackPurchase(checkoutPlanId.value)
    trackPaid(checkoutPlanId.value, false)
    let cvId = packCvId.value
    packCvId.value = null

    // A free account at its one CV could not save this one before paying. The
    // pack lifts the limit, so it is saved now; the single PDF is handed over
    // right here, on the credit just bought.
    const cvData = cv()
    if (!cvId && cvLimitReached.value && cvData) {
      if (checkoutPlanId.value === 'one-time') {
        const ok = await exportToPDF({
          cvData: { ...cvData, showQRCode: false },
          template: template(),
          filename: `${fileBase()}.pdf`,
          wantClean: true,
          generationId: toValue(options.generationId),
        })
        trackDownload('paid', ok)
        if (ok) return true
      } else {
        cvId = await persistCV()
      }
    }

    await navigateTo(paidCvPath(checkoutPlanId.value, cvId))
    return true
  }

  function watchPackPayment() {
    stopPackPoll()
    let attempts = 0
    packPoll = setInterval(async () => {
      attempts++
      if (attempts > MAX_POLLS) {
        stopPackPoll()
        return
      }
      await checkPackPayment()
    }, POLL_INTERVAL_MS)
  }

  async function openCheckout() {
    const plan = getPlan(checkoutPlanId.value)
    if (!plan || !user.value) return

    isStartingCheckout.value = true
    error.value = null
    try {
      const cvId = await persistCV()
      await checkPremiumStatus()
      packBaseline = { isPremium: isPremium.value, exportCredits: exportCredits.value }
      const url = await createCheckoutSession({
        variantId: plan.variantId,
        userId: user.value.id,
        email: user.value.email || '',
        planId: plan.id,
        discountCode: welcomeCode(plan.id),
        redirectUrl: returnUrl(plan.id, cvId),
        // What was paid for this CV before comes off the pack (server/utils/packCredit.ts).
        generationId: toValue(options.generationId) ?? undefined,
      })

      if (!url) {
        error.value = t('premium.checkoutError')
        return
      }
      packCvId.value = cvId
      checkoutUrl.value = url
      trackCheckout(plan.id, false)
      watchPackPayment()
    } catch (err) {
      console.error('Failed to open checkout:', err)
      error.value = t('premium.checkoutError')
    } finally {
      isStartingCheckout.value = false
    }
  }

  /**
   * Paid CTAs. Signed-in visitors go through their account, so the purchase
   * shows up where they expect it. Signed out, both products are bought as a
   * guest: the account is what the pack needs afterwards, not a toll in front
   * of the payment. The tailored PDF is a guest purchase for everyone: what it
   * buys is the parked tailoring behind its token, not anything on an account.
   */
  async function buy(planId: OfferPlanId) {
    if (!cv() || !getPlan(planId)) return
    checkoutPlanId.value = planId

    if (isAuthenticated.value && !onTailoring(planId)) {
      await openCheckout()
      return
    }

    await openGuestCheckout(planId)
  }

  /**
   * The sign-up under the offers means two different things: before a payment it
   * opens the checkout, after a paid pack it hands that purchase to the new
   * account. `packAwaitingAccount` is what tells them apart.
   */
  async function onSignedIn() {
    showSignup.value = false

    if (packAwaitingAccount.value && guestToken.value) {
      isClaiming.value = true
      const opened = await claimAndOpen(guestToken.value)
      isClaiming.value = false
      if (!opened) error.value = t('landing.builder.pack.claimError')
      return
    }

    await openCheckout()
  }

  function onConfirmEmail(email: string) {
    showSignup.value = false
    confirmEmailSentTo.value = email
  }

  /** Signed out: make the account first, the CV is waiting on the other side. */
  function saveForLater() {
    const cvData = cv()
    if (!cvData) return

    setPendingCV(cvData)
    setPendingTemplate(template())
    setPendingGeneration(toValue(options.generationId) ?? null)
    journey.track('cv_save', { generation_id: toValue(options.generationId) ?? null, how: 'later', ...extra() })
    return navigateTo(`/registration?from=${options.signupSource ?? 'cv-generator'}`)
  }

  /** Signed in: the CV goes on the account and the editor opens on it. */
  async function saveAndEdit() {
    journey.track('cv_save', { generation_id: toValue(options.generationId) ?? null, how: 'edit', ...extra() })
    const cvId = await persistCV()
    if (!cvId) {
      error.value = t(cvLimitReached.value ? 'homepage.aiGenerator.cvLimit' : 'homepage.aiGenerator.saveError')
      return
    }
    // A posting the CV was just tailored to: the editor opens on that, and tailors.
    return navigateTo(`/cv/${cvId}${hasPendingPosting() ? '?section=tailor' : ''}`)
  }

  // Closing the checkout by hand can still mean "paid on the last screen", so
  // the purchase is checked once more before the watching stops.
  watch(checkoutUrl, (url) => {
    if (url) return

    if (!guestPaid.value && guestToken.value) {
      stopGuestPoll()
      checkGuestPayment(guestToken.value)
    }

    // packPoll: a checkout whose CV could not be saved first is watched too.
    if (packCvId.value || packPoll) {
      stopPackPoll()
      checkPackPayment()
    }
  })

  onBeforeUnmount(() => {
    stopGuestPoll()
    stopPackPoll()
  })

  return {
    // State
    error,
    isExporting,
    isStartingCheckout,
    isClaiming,
    isScriptLoading,
    checkoutUrl,
    checkoutPlanId,
    showSignup,
    signupOfferLabel,
    confirmEmailSentTo,
    guestPaid,
    guestDownloadUrl,
    guestToken,
    guestClaimUrl,
    packAwaitingAccount,
    canBuyPack,
    canBuySingle,
    canBuyTailored,
    canBuyKit,
    guestPlan,
    isPremium,
    isAuthenticated,
    jobPackPrice,
    exportCreditPrice,

    // Actions
    downloadFree,
    followUpDownload,
    downloadPaid,
    buy,
    onSignedIn,
    onConfirmEmail,
    saveForLater,
    saveAndEdit,
    finishPack: () => { showSignup.value = true },
  }
}
